<template>
  <div class="colheita-component">
    <h1>{{ t('colheita.title') }}</h1>
    
    <!-- Existing Form for Adding New Entries -->
    <div v-for="(entry, index) in binEntries" :key="index" class="entry-row">
      <el-date-picker 
        v-model="entry.date" 
        type="date" 
        :placeholder="t('colheita.select_date')"
        :max="today"
        :class="{ 'is-error': errors[index]?.date }"
      />
      <el-select 
        v-model="entry.setor" 
        :placeholder="t('colheita.select_setor')"
        :class="{ 'is-error': errors[index]?.setor }"
      >
        <el-option
          v-for="n in 10"
          :key="n"
          :label="`${t('colheita.setor')} ${n}`"
          :value="`${n}`"
        />
      </el-select>
      <el-select 
        v-model="entry.tipo" 
        :placeholder="t('colheita.select_type')"
        :class="{ 'is-error': errors[index]?.tipo }"
      >
        <el-option 
          v-for="tipo in tipos" 
          :key="tipo.id" 
          :label="tipo.nome" 
          :value="tipo.id" 
        />
      </el-select>
      <el-input-number 
        v-model="entry.quantity" 
        :min="1" 
        :max="99"
        :placeholder="t('colheita.quantity')"
        :class="{ 'is-error': errors[index]?.quantity }"
      />
      <el-button @click="removeEntry(index)" type="danger" plain>
        <el-icon><Delete /></el-icon>
        {{ t('common.delete') }}
      </el-button>
    </div>
    <el-button 
      @click="addEntry" 
      type="primary" 
      :disabled="!canAddNewEntry"
    >{{ t('colheita.add_entry') }}</el-button>
    <el-button 
      @click="showSummary" 
      type="success" 
      :disabled="binEntries.length === 0"
    >{{ t('colheita.review_submit') }}</el-button>
<hr />
    <!-- Button to fetch existing entries -->
    <el-button type="primary" @click="fetchEntries" style="margin-top: 20px;">Load Existing Entries</el-button>
    <el-button type="primary" @click="showEchart" style="margin-top: 20px;">Show Chart</el-button>

    <!-- Updated Filter Controls -->
    <div class="filter-controls" style="margin-top: 20px;">
      <el-select 
        v-model="filterSector"
        clearable
        :placeholder="t('colheita.selectSector')"
        style="width: 200px; margin-right: 10px;"
      >
        <el-option
          v-for="sector in availableSectors"
          :key="sector"
          :label="`${t('colheita.setor')} ${sector}`"
          :value="sector"
        />
      </el-select>
      <el-input 
        v-model="filterText" 
        :placeholder="t('colheita.filter_placeholder')" 
        style="width: 200px;"
      />
    </div>

    <!-- Table for displaying existing entries -->
    <el-table v-if="entries.length" :data="filteredEntries" style="width: 100%; margin-top: 20px;">
      <el-table-column prop="date" :label="t('colheita.date')" />
      <el-table-column prop="setor" :label="t('colheita.sector')" />
      <el-table-column prop="tipoRef.nome" :label="t('colheita.type')" />
      <el-table-column prop="quantity" :label="t('colheita.quantity')" />
    </el-table>

    <!-- Summary Table -->
    <el-table v-if="summary.length" :data="filteredSummary" style="width: 100%; margin-top: 20px;">
      <el-table-column prop="setor" :label="t('colheita.sector')" />
      <el-table-column prop="tipoRef.nome" :label="t('colheita.apple_variety')" />
      <el-table-column prop="quantity" :label="t('colheita.total_quantity')" />
    </el-table>

    <!-- Add this at the end of your template, before the closing div -->
    <el-dialog
      v-model="chartDialogVisible"
      :title="t('colheita.dailyChart')"
      width="80%"
    >
      <div ref="chartContainer" style="width: 100%; height: 400px;"></div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { Delete } from '@element-plus/icons-vue';
import { useI18n } from 'vue-i18n';
import Swal from 'sweetalert2';
import { ColheitaBinsApi } from '../utils/ColheitaBinsApi';
import type { BinEntry, Tipo } from '../types/colheita';
import * as echarts from 'echarts';

const { t } = useI18n();

interface EntryError {
  date?: boolean;
  setor?: boolean;
  tipo?: boolean;
  quantity?: boolean;
}

const binEntries = ref<BinEntry[]>([]);
const errors = ref<EntryError[]>([]);
const today = new Date();
const entries = ref<Array<BinEntry>>([]);
const filterText = ref('');
const filterSector = ref<string | null>(null);
const summary = ref<Array<BinEntry>>([]);
const errorMessage = ref<{ title: string; message: string } | null>(null);
const tipos = ref<Tipo[]>([]);
const tipoMap = ref<Map<number, Tipo>>(new Map());
const chartDialogVisible = ref(false);
const chartContainer = ref<HTMLElement | null>(null);
let chartInstance: echarts.ECharts | null = null;

// 
// Add new refs
// const selectedSector = ref<string | null>(null);

// Compute available sectors
const availableSectors = computed(() => {
  const sectors = new Set<string>();
  entries.value.forEach(entry => {
    if (entry.setor) {
      sectors.add(entry.setor);
    }
  });
  return Array.from(sectors).sort();
});

// Fetch tipos when the component is mounted
onMounted(async () => {
  try {
    tipos.value = await ColheitaBinsApi.getTipos();
    tipos.value.forEach(tipo => {
      tipoMap.value.set(tipo.id, tipo);
    });
  } catch (error) {
    console.error('Failed to fetch tipos:', error);
    errorMessage.value = {
      title: 'Error',
      message: 'Failed to load tipos. Please try again later.',
    };
  }
});

const validateEntry = (entry: BinEntry, index: number): boolean => {
  const error: EntryError = {};
  let hasError = false;

  // Date validation
  if (!entry.date || entry.date > today) {
    error.date = true;
    hasError = true;
  }

  // Setor validation
  if (!entry.setor) {
    error.setor = true;
    hasError = true;
  }

  // Tipo validation
  if (!entry.tipo) {
    error.tipo = true;
    hasError = true;
  }

  // Quantity validation
  if (!entry.quantity || entry.quantity < 1 || entry.quantity > 99) {
    error.quantity = true;
    hasError = true;
  }

  errors.value[index] = error;
  return !hasError;
};

const canAddNewEntry = computed(() => {
  if (binEntries.value.length === 0) return true;
  const lastEntry = binEntries.value[binEntries.value.length - 1];
  return validateEntry(lastEntry, binEntries.value.length - 1);
});

const addEntry = () => {
  if (!canAddNewEntry.value) return;
  
  binEntries.value.push({
    date: null,
    setor: null,
    tipo: null,
    tipoRef: null,
    quantity: null,
    lastchanged: null,
    lastchangedby: null
  });
  errors.value.push({});
};

const removeEntry = (index: number) => {
  binEntries.value.splice(index, 1);
  errors.value.splice(index, 1);
};

const showSummary = async () => {
  // Validate all entries
  const isValid = binEntries.value.every((entry, index) => validateEntry(entry, index));
  
  if (!isValid) {
    Swal.fire({
      title: t('colheita.error'),
      text: t('colheita.validation_error'),
      icon: 'error'
    });
    return;
  }

  const confirmed = await Swal.fire({
    title: t('colheita.review_title'),
    html: `
      <div style="text-align: left;">
        ${binEntries.value.map((entry, index) => `
          <p><strong>${t('colheita.entry')} ${index + 1}:</strong></p>
          <p>${t('colheita.date')}: ${entry.date?.toLocaleDateString()}</p>
          <p>${t('colheita.setor')}: ${entry.setor}</p>
          <p>${t('colheita.type')}: ${tipoMap.value.get(entry.tipo ?? 0)?.nome ?? '---'}</p>
          <p>${t('colheita.quantity')}: ${entry.quantity}</p>
          <hr>
        `).join('')}
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: t('colheita.submit'),
    cancelButtonText: t('colheita.cancel'),
    icon: 'info'
  });

  if (confirmed.isConfirmed) {
    submitEntries();
  }
};

const fetchEntries = async () => {
  try {
    entries.value = await ColheitaBinsApi.getAll();
    calculateSummary();
  } catch (error) {
    console.error('Error fetching entries:', error);
  }
};

const submitEntries = async () => {
  try {
    await ColheitaBinsApi.createBulk(binEntries.value);
    Swal.fire(t('colheita.success'), t('colheita.submit_success'), 'success');
    binEntries.value = [];
    errors.value = [];
  } catch (error) {
    Swal.fire(t('colheita.error'), t('colheita.submit_error'), 'error');
  }
};

// Calculate summary totals per sector and apple variety
const calculateSummary = () => {
  const totals: Record<string, BinEntry> = {};

  entries.value.forEach(entry => {
    const key = `${entry.setor}-${entry.tipo}`;
    if (!totals[key]) {
      totals[key] = { setor: entry.setor, tipo: entry.tipo, tipoRef: entry.tipoRef, quantity: entry.quantity, date: entry.date, lastchanged: entry.lastchanged, lastchangedby: entry.lastchangedby };
    } else {
        totals[key].quantity = (totals[key].quantity ?? 0) + (entry.quantity ?? 0);
    }
  });

  console.log(totals);

  summary.value = Object.values(totals);
};

// Filtered entries based on user input
const filteredEntries = computed(() => {
  return entries.value.filter(entry => {
    const matchesText = !filterText.value || 
      entry.setor?.toString().toLowerCase().includes(filterText.value.toLowerCase()) ||
      entry.tipoRef?.nome.toLowerCase().includes(filterText.value.toLowerCase());
    
    const matchesSector = !filterSector.value || entry.setor === filterSector.value;
    
    return matchesText && matchesSector;
  });
});

// Add filtered summary
const filteredSummary = computed(() => {
  return summary.value.filter(entry => {
    const matchesText = !filterText.value || 
      entry.setor?.toString().toLowerCase().includes(filterText.value.toLowerCase()) ||
      entry.tipoRef?.nome.toLowerCase().includes(filterText.value.toLowerCase());
    
    const matchesSector = !filterSector.value || entry.setor === filterSector.value;
    
    return matchesText && matchesSector;
  });
});

const showEchart = () => {
  if (entries.value.length === 0) {
    Swal.fire({
      title: t('colheita.error'),
      text: t('colheita.noDataToDisplay'),
      icon: 'warning'
    });
    return;
  }

  chartDialogVisible.value = true;
  
  // Wait for the dialog to be mounted and then initialize the chart
  setTimeout(() => {
    if (chartContainer.value && !chartInstance) {
      chartInstance = echarts.init(chartContainer.value);
    }
    updateChart();
  }, 100);
};

const updateChart = () => {
  if (!chartInstance) return;

  // Group entries by date and sum quantities
  const dailySums: Record<string, number> = {};
  
  // Find min and max dates
  let minDate: Date | null = null;
  let maxDate: Date | null = null;
  
  // Filter entries by selected sector
  const filteredChartEntries = filterSector.value
    ? entries.value.filter(entry => entry.setor === filterSector.value)
    : entries.value;
  
  filteredChartEntries.forEach(entry => {
    if (!entry.date) return;
    const date = new Date(entry.date);
    if (!minDate || date < minDate) minDate = date;
    if (!maxDate || date > maxDate) maxDate = date;
  });

  // Ensure maxDate is a valid Date before comparison
  if (!maxDate) {
    maxDate = new Date(); // Set to current date if no maxDate found
  }

  // Create array of all dates between min and max
  const allDates: string[] = [];
  const currentDate = new Date(minDate ?? new Date());
  
  while (currentDate <= maxDate) {
    const dateStr = currentDate.toISOString().split('T')[0];
    allDates.push(dateStr);
    dailySums[dateStr] = 0; // Initialize all dates with 0
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Fill in the actual quantities
  filteredChartEntries.forEach(entry => {
    if (!entry.date) return;
    const dateStr = new Date(entry.date).toISOString().split('T')[0];
    dailySums[dateStr] = (dailySums[dateStr] || 0) + (entry.quantity || 0);
  });

  // Sort dates chronologically
  const sortedDates = allDates.sort();

  // Format dates with weekdays
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const formatDateWithWeekday = (dateStr: string) => {
    const date = new Date(dateStr);
    const weekday = weekdays[date.getDay()];
    return `${weekday}, ${date.toLocaleDateString()}`;
  };

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params: any) => {
        const date = new Date(params[0].name);
        const weekday = weekdays[date.getDay()];
        const value = params[0].value;
        return `${weekday}, ${date.toLocaleDateString()}: ${value}`;
      }
    },
    title: {
      text: filterSector.value 
        ? t('colheita.dailyTotalsSector', { sector: filterSector.value })
        : t('colheita.dailyTotals'),
      left: 'center'
    },
    xAxis: {
      type: 'category',
      data: sortedDates.map(formatDateWithWeekday),
      axisLabel: {
        rotate: 45,
        interval: 0,
        fontSize: 11,
        width: 100,
        overflow: 'break'
      }
    },
    yAxis: {
      type: 'value',
      name: t('colheita.quantity')
    },
    series: [{
      name: t('colheita.quantity'),
      type: 'bar',
      data: sortedDates.map(date => dailySums[date]),
      itemStyle: {
        color: '#166b43'
      }
    }],
    grid: {
      left: '3%',
      right: '4%',
      bottom: '20%',
      containLabel: true
    }
  };

  chartInstance.setOption(option);
};

// Clean up chart when dialog closes
const handleDialogClose = () => {
  if (chartInstance) {
    chartInstance.dispose();
    chartInstance = null;
  }
};

// Watch for dialog visibility changes
watch(chartDialogVisible, (newValue) => {
  if (!newValue) {
    handleDialogClose();
  }
});

// Watch for filter changes to update the chart
watch(filterSector, () => {
  if (chartInstance) {
    updateChart();
  }
});

// Add window resize handler
const handleResize = () => {
  chartInstance?.resize();
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
  handleDialogClose();
});

</script>

<style scoped>
.colheita-component {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.entry-row {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
}

.entry-row > * {
  flex: 1;
}

.entry-row > .el-input-number {
  width: 120px;
}

.is-error {
  border-color: #F56C6C;
}

.el-dialog :deep(.el-dialog__body) {
  padding: 20px;
}

.filter-controls {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
}
</style>
